import { ArrowRight, Check } from "lucide-react";
import { IMAGES, STATS } from "@/data/content";
import KineticHero from "@/components/KineticHero";
import ImageReveal from "@/components/ImageReveal";
import MagneticButton from "@/components/MagneticButton";
import SplitText from "@/components/SplitText";
import Reveal from "@/components/Reveal";

export default function About() {
  return (
    <div data-testid="about-page" className="overflow-x-clip">
      <KineticHero
        eyebrow="About GoodLiving Strategy"
        image={IMAGES.skyline}
        lines={[
          "Behind every",
          "global purchase,",
          <span key="s" className="italic text-gl-gold">a strategy.</span>,
        ]}
        subtitle="Most consultancies hand you listings. We hand you a strategy — and the discipline to execute it."
      />

      {/* MISSION + FOUNDER */}
      <section className="py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <ImageReveal
                src={IMAGES.founder}
                alt="Founder, GoodLiving Strategy"
                className="aspect-[3/4] w-full"
              />
              <Reveal delay={0.25}>
                <div className="absolute -bottom-8 -right-6 md:-right-10 bg-gl-gold text-gl-bg p-7 max-w-[260px]">
                  <div className="font-serif-display text-2xl leading-tight">
                    Adolphus Clark
                  </div>
                  <p className="text-sm mt-2">Real Estate Investor &amp; Opportunity Strategist (DBA Doctoral Candidate)</p>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-baseline gap-5 mb-8">
                <span className="font-serif-display text-2xl text-gl-gold">01</span>
                <span className="h-px flex-1 bg-gl-gold/25" />
                <span className="text-gl-text2 uppercase tracking-[0.3em] text-xs">Our Mission</span>
              </div>
            </Reveal>
            <SplitText
              as="h2"
              animateOnLoad={false}
              className="font-serif-display font-light leading-[1.02] text-gl-text text-4xl md:text-6xl"
              lines={["Turning aspiration", <span key="a">into <span className="italic text-gl-gold">acquisition.</span></span>]}
            />
            <Reveal delay={0.15}>
              <p className="mt-8 text-gl-text2 text-lg font-light leading-relaxed max-w-xl">
                GoodLiving Strategy was founded on a simple conviction: owning
                property abroad shouldn't be reserved for the already-wealthy. It
                should belong to the financially intentional.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-5 text-gl-text2 text-lg font-light leading-relaxed max-w-xl">
                We blend behavioural finance with creative deal structuring,
                guiding ambitious individuals from "someday" to signed contracts
                across the world's most desirable markets. Replace this placeholder
                with your authentic voice and journey.
              </p>
            </Reveal>
            <div className="mt-9 space-y-4">
              {[
                "Behaviour-led financial coaching",
                "Cross-border financing expertise",
                "Hands-on acquisition guidance",
              ].map((t, i) => (
                <Reveal key={t} delay={0.28 + i * 0.08}>
                  <div className="flex items-center gap-4">
                    <span className="w-5 h-5 flex-shrink-0 border border-gl-gold/50 flex items-center justify-center">
                      <Check size={12} className="text-gl-gold" />
                    </span>
                    <span className="text-gl-text2 font-light">{t}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-gl-bg2 border-y border-gl-gold/15 py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-12">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div className="font-serif-display text-5xl md:text-6xl text-gl-gold">{s.value}</div>
              <div className="mt-3 text-sm text-gl-text2">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY — pull-quote */}
      <section className="py-28 md:py-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="text-gl-gold uppercase tracking-[0.3em] text-xs mb-10 block text-center">
              Our Philosophy
            </span>
          </Reveal>
          <figure className="relative">
            <Reveal>
              <span
                aria-hidden="true"
                className="font-serif-display text-gl-gold/25 text-[9rem] md:text-[13rem] leading-[0.7] block text-center select-none"
              >
                &ldquo;
              </span>
            </Reveal>
            <blockquote className="-mt-10 md:-mt-16">
              <SplitText
                as="p"
                animateOnLoad={false}
                className="font-serif-display text-3xl md:text-5xl font-light text-gl-text leading-[1.2] text-center"
                lines={[
                  "Wealth abroad is built",
                  "on habits at home.",
                  <span key="c" className="italic text-gl-gold">Get the structure right.</span>,
                ]}
              />
            </blockquote>
            <Reveal delay={0.18}>
              <figcaption className="mt-10 flex items-center justify-center gap-4">
                <span className="h-px w-10 bg-gl-gold/50" />
                <span className="text-gl-text2 tracking-wide text-sm md:text-base">
                  Adolphus Clark, <span className="text-gl-text">GoodLiving Strategy</span>
                </span>
                <span className="h-px w-10 bg-gl-gold/50" />
              </figcaption>
            </Reveal>
          </figure>
          <Reveal delay={0.28}>
            <div className="mt-14 flex justify-center">
              <MagneticButton
                to="/contact"
                data-testid="about-cta"
                className="px-9 py-4 bg-gl-gold text-gl-bg font-medium tracking-wide hover:bg-gl-goldHover transition-colors duration-300"
              >
                Book Your Consultation
                <ArrowRight size={18} />
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
